import type { Row } from '@tanstack/react-table';
import type { VehicleWithDriverAndPeripheralsResponse } from '~/api/generated-api';
import type { VehicleAssignmentItem } from '~/common/models/vehicle.model';

/**
 * Maps API response from getVehiclesWithDriversAndPeripherals to VehicleAssignmentItem format.
 *
 * @param vehicle - The vehicle data from the API response
 * @param organizationId - The organization ID to associate with the vehicle
 * @returns VehicleAssignmentItem for use in the assignment modal
 */
export const mapToVehicleAssignmentItem = (
  vehicle: VehicleWithDriverAndPeripheralsResponse,
  organizationId?: string
): VehicleAssignmentItem => ({
  id: vehicle.vin ?? '',
  name: vehicle.name ?? '',
  organization: organizationId ? { id: organizationId, name: '' } : undefined,
  fleet: vehicle.fleetId ? { id: vehicle.fleetId, name: '' } : undefined,
  make: vehicle.make,
  model: vehicle.model,
  year: vehicle.year,
  imageSourceUrl: vehicle.imageSourceUrl,
  assignedDevices: vehicle.assignedDevices,
  assignedDriver: vehicle.assignedDriver,
});

/**
 * Maps an array of API response vehicles to VehicleAssignmentItem array.
 *
 * @param vehicles - Array of vehicle data from the API response
 * @param organizationId - The organization ID to associate with the vehicles
 * @returns Array of VehicleAssignmentItem for use in the assignment modal
 */
export const mapToVehicleAssignmentItems = (
  vehicles: VehicleWithDriverAndPeripheralsResponse[],
  organizationId?: string
): VehicleAssignmentItem[] =>
  vehicles.map((vehicle) =>
    mapToVehicleAssignmentItem(vehicle, organizationId)
  );

/**
 * Custom sorting function for driver column.
 *
 * Sorting behavior:
 * - Ascending: Vehicles WITH drivers appear first, sorted alphabetically by name.
 *              Vehicles WITHOUT drivers appear last.
 * - Descending: Order is reversed by the table (vehicles without drivers first,
 *               then drivers in reverse alphabetical order).
 *
 * This groups vehicles with assigned drivers together for better UX.
 */
export const sortByDriverName = (
  rowA: Row<VehicleAssignmentItem>,
  rowB: Row<VehicleAssignmentItem>
): number => {
  const driverA = rowA.original.assignedDriver?.name ?? '';
  const driverB = rowB.original.assignedDriver?.name ?? '';

  // Group vehicles with drivers before those without (in ascending order)
  if (driverA && !driverB) return -1;
  if (!driverA && driverB) return 1;

  // If both have drivers (or both don't), sort alphabetically
  return driverA.localeCompare(driverB);
};

/**
 * Custom sorting function for devices column.
 *
 * Sorting behavior:
 * - Ascending: Vehicles WITH devices appear first, sorted by total device count (low to high).
 *              Vehicles WITHOUT devices appear last.
 * - Descending: Order is reversed by the table (vehicles without devices first,
 *               then devices in high to low count order).
 *
 * This groups vehicles with assigned devices together for better UX.
 */
export const sortByDeviceCount = (
  rowA: Row<VehicleAssignmentItem>,
  rowB: Row<VehicleAssignmentItem>
): number => {
  const devicesA = rowA.original.assignedDevices;
  const devicesB = rowB.original.assignedDevices;

  const countA =
    devicesA?.reduce((sum, device) => sum + (device.count ?? 0), 0) ?? 0;
  const countB =
    devicesB?.reduce((sum, device) => sum + (device.count ?? 0), 0) ?? 0;

  // Group vehicles with devices before those without (in ascending order)
  if (countA > 0 && countB === 0) return -1;
  if (countA === 0 && countB > 0) return 1;

  // If both have devices (or both don't), sort by total count
  return countA - countB;
};

/**
 * Helper function to get total device count for a vehicle.
 */
const getDeviceCount = (vehicle: VehicleAssignmentItem): number =>
  vehicle.assignedDevices?.reduce(
    (sum, device) => sum + (device.count ?? 0),
    0
  ) ?? 0;

/**
 * Multi-level sorting function for vehicle assignment data.
 *
 * Sorting priority (all in descending order - items with values appear first):
 * 1. Primary: Vehicles WITH drivers first, then alphabetically by driver name
 * 2. Secondary: Vehicles WITH devices first, then by total device count (high to low)
 * 3. Tertiary: Alphabetically by vehicle name
 *
 * @param vehicles - Array of VehicleAssignmentItem to sort
 * @returns New sorted array (does not mutate original)
 */
export const sortVehicleAssignmentData = (
  vehicles: VehicleAssignmentItem[]
): VehicleAssignmentItem[] => {
  return [...vehicles].sort((a, b) => {
    // Primary sort: Drivers (vehicles with drivers first, then alphabetically)
    const driverA = a.assignedDriver?.name ?? '';
    const driverB = b.assignedDriver?.name ?? '';

    if (driverA && !driverB) return -1;
    if (!driverA && driverB) return 1;
    if (driverA && driverB) {
      const driverCompare = driverA.localeCompare(driverB);
      if (driverCompare !== 0) return driverCompare;
    }

    // Secondary sort: Devices (vehicles with devices first, then by count high to low)
    const countA = getDeviceCount(a);
    const countB = getDeviceCount(b);

    if (countA > 0 && countB === 0) return -1;
    if (countA === 0 && countB > 0) return 1;
    if (countA !== countB) return countB - countA; // High to low

    // Tertiary sort: Vehicle name (alphabetically)
    const nameA = a.name ?? '';
    const nameB = b.name ?? '';
    return nameA.localeCompare(nameB);
  });
};
